// Haversine distance in km between two lat/lon points
export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// Parse geographic coordinates entered as free text in common formats:
//  - Decimal degrees: "50.123, 14.456" / "50.123 14.456" / "50.123N 14.456E"
//  - Degrees + decimal minutes: "50 7.383N, 14 27.367E"
//  - Degrees, minutes, seconds: "50°7'23\"N 14°27'22\"E"
// Returns { lat, lon } or null if the text can't be parsed.
export function parseCoordinates(text) {
  if (!text || typeof text !== 'string') return null
  const s = text.trim()
  if (!s) return null

  // Split into two coordinate tokens: prefer comma, else whitespace,
  // but keep DMS groups (with internal spaces) together by splitting on
  // a comma first; if there's no comma, split on the hemisphere letter
  // or on a run of whitespace that separates two "N/S ... E/W" halves.
  let parts
  if (s.includes(',')) {
    parts = s.split(',')
  } else {
    // Try to split after a hemisphere letter (N/S) that ends the first coordinate
    const afterHemisphere = s.match(/^(.*?[NSns])\s+(.*)$/)
    if (afterHemisphere) {
      parts = [afterHemisphere[1], afterHemisphere[2]]
    } else {
      // Fallback: split in half by whitespace groups (decimal degrees "50.1 14.4")
      const tokens = s.split(/\s+/)
      if (tokens.length === 2) {
        parts = tokens
      } else {
        return null
      }
    }
  }
  if (parts.length !== 2) return null

  const lat = parseSingleCoordinate(parts[0], 'lat')
  const lon = parseSingleCoordinate(parts[1], 'lon')
  if (lat == null || lon == null) return null
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
  return { lat, lon }
}

// Parse a single coordinate token (latitude or longitude) into signed decimal degrees.
function parseSingleCoordinate(raw, axis) {
  let str = raw.trim()
  if (!str) return null

  // Hemisphere letter, prefix or suffix (N/S/E/W, case-insensitive)
  let sign = 1
  const hemMatch = str.match(/^([NSEWnsew])\s*(.*)$|^(.*?)\s*([NSEWnsew])$/)
  if (hemMatch) {
    const hem = (hemMatch[1] || hemMatch[4] || '').toUpperCase()
    const rest = hemMatch[1] ? hemMatch[2] : hemMatch[3]
    if (hem) {
      if (hem === 'S' || hem === 'W') sign = -1
      str = rest.trim()
    }
  }

  // Normalize DMS separators (° ' " and letters d/m/s) to spaces
  str = str
    .replace(/[°º]/g, ' ')
    .replace(/['′]/g, ' ')
    .replace(/["″]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const nums = str.split(' ').map(v => v.replace(',', '.')).filter(Boolean).map(Number)
  if (nums.some(n => Number.isNaN(n))) return null

  let value
  if (nums.length === 1) {
    value = nums[0] // decimal degrees
  } else if (nums.length === 2) {
    value = nums[0] + nums[1] / 60 // degrees + decimal minutes
  } else if (nums.length === 3) {
    value = nums[0] + nums[1] / 60 + nums[2] / 3600 // degrees, minutes, seconds
  } else {
    return null
  }

  return value * sign
}

// Normalize Czech string for accent-insensitive search
export function normalizeCzech(str) {
  if (!str) return ''
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}